import * as React from 'react';

import cx from 'classnames';

import BoxBody from 'components/BoxBody';
import Button, { ButtonColorUnion } from 'components/Button';
import Buttons from 'components/Buttons';
import Dialog, { Props as DialogProps } from 'components/Dialog';
import LoadingIndicator from 'components/LoadingIndicator';
import createChainedFunction from 'utils/createChainedFunction';
import errorLogger from 'utils/errorLogger';

import './styles/ConfirmDialog.scss';

// TODO
// * Reconcile `action*` & `confirm*` prop & method names
// * Fix `className`s

export type Props = DialogProps & {
  actionButtonColor?: ButtonColorUnion;
  actionButtonText?: string;
  cancelButtonColor?: ButtonColorUnion;
  cancelButtonText?: string;
  cancelEventKey?: string | undefined;
  cancelEventProperties?: any;
  children?: React.ReactNode;
  /** Disable action button */
  confirmDisabled?: boolean;
  confirmEventKey?: string | undefined;
  confirmEventProperties?: any;
  /** `border` between `header` & body */
  divided?: boolean;
  /* deprecated, old ui event tracking */
  eventContext?: string;
  /* deprecated, old ui event tracking */
  eventScreenName?: string;
  hideCancel?: boolean;
  /** Cancel button action */
  onCancel?: () => void;
  /** Action button action */
  onConfirm?: () => void;
  preConfirmPromise?: () => Promise<any>;
  onRequestHide?: () => void;
  staticModal?: boolean;
};

type State = {
  loading: boolean;
};

export default class ConfirmDialog extends React.Component<Props, State> {
  state: State = {
    loading: false,
  };

  handleConfirm = () => {
    const { onConfirm } = this.props;
    if (onConfirm) {
      onConfirm();
    }
  };

  handleRequestHide = () => {
    const { onRequestHide } = this.props;
    if (onRequestHide) {
      onRequestHide();
    }
  };

  handleCancelButtonClick = () => {
    const { onCancel } = this.props;
    this.handleRequestHide();

    if (onCancel) {
      onCancel();
    }
  };

  handleConfirmButtonClick = () => {
    const { preConfirmPromise, staticModal } = this.props;
    const emptyPromiseFn = () => Promise.resolve();
    const promiseFn = preConfirmPromise || emptyPromiseFn;
    this.setState({ loading: true });

    if (staticModal !== false) {
      promiseFn()
        .then(this.handleConfirm)
        .then(this.handleRequestHide)
        .catch(errorLogger('Static modal pre confirm failure'));
    } else {
      promiseFn()
        .then(this.handleConfirm)
        .catch(errorLogger('Pre confirm failure'));
    }
  };

  renderBody = () => {
    const {
      actionButtonColor,
      actionButtonText,
      cancelButtonText,
      cancelButtonColor,
      cancelEventKey,
      cancelEventProperties,
      children,
      confirmDisabled,
      confirmEventKey,
      confirmEventProperties,
      eventScreenName,
      hideCancel,
      size,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <LoadingIndicator />;
    } else {
      return (
        <div>
          <div className={cx({ 'ConfirmDialog-text': !!children })}>{children}</div>
          <Buttons axis="y">
            <Button
              color={actionButtonColor}
              disabled={confirmDisabled}
              key="confirm-action"
              className="action"
              onClick={this.handleConfirmButtonClick}
              eventElementName="ConfirmDialog-actionButton"
              eventScreenName={eventScreenName}
              eventKey={confirmEventKey}
              eventProperties={confirmEventProperties}
              data-test-selector="confirm_dialog_action_button">
              {actionButtonText || 'Yes'}
            </Button>
            {!hideCancel && (
              <Button
                color={cancelButtonColor}
                level={!size || size === 'xsmall' ? 'secondary' : 'text'}
                key="confirm-cancel"
                className="cancel"
                onClick={this.handleCancelButtonClick}
                eventElementName="ConfirmDialog-cancelButton"
                eventKey={cancelEventKey}
                eventProperties={cancelEventProperties}
                eventScreenName={eventScreenName}>
                {cancelButtonText || 'No'}
              </Button>
            )}
          </Buttons>
        </div>
      );
    }
  };

  render() {
    const { className, divided, onCancel, onRequestHide, size, staticModal, ...props } = this.props;

    return (
      <Dialog
        {...props}
        size={size || 'xsmall'}
        className={cx('ConfirmDialog', className)}
        onRequestHide={createChainedFunction(onCancel, onRequestHide)}>
        <BoxBody divided={divided || false}>{this.renderBody()}</BoxBody>
      </Dialog>
    );
  }
}
